import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Upload, Download, Trash2, Loader2, History } from "lucide-react";

const DOC_TYPES = [
  { value: "protokoll", label: "Prüfprotokoll" }, { value: "wartungsbericht", label: "Wartungsbericht" },
  { value: "bescheinigung", label: "Prüfbescheinigung" }, { value: "messprotokoll", label: "Messprotokoll" },
  { value: "rechnung", label: "Rechnung" }, { value: "reparaturbericht", label: "Reparaturbericht" },
  { value: "herstellerunterlage", label: "Herstellerunterlage" }, { value: "zertifikat", label: "Zertifikat" },
  { value: "maengelbericht", label: "Mängelbericht" }, { value: "foto", label: "Foto" }, { value: "sonstiges", label: "Sonstiges" },
];

export default function DocumentsDialog({ open, onClose, inspection, canWrite, canDelete }) {
  const [docs, setDocs] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [meta, setMeta] = useState({ name: "", doc_type: "protokoll", pruef_date: "", supersedes_id: "" });
  const fileRef = useRef(null);

  const load = useCallback(() => {
    if (!inspection) return;
    api.get(`/inspections/${inspection.id}/documents`).then((r) => setDocs(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [inspection]);
  useEffect(() => { if (open) { setDocs(null); setMeta({ name: "", doc_type: "protokoll", pruef_date: "", supersedes_id: "" }); load(); } }, [open, load]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", meta.name || file.name);
      fd.append("doc_type", meta.doc_type);
      fd.append("pruef_date", meta.pruef_date || "");
      if (meta.supersedes_id) fd.append("supersedes_id", meta.supersedes_id);
      await api.post(`/inspections/${inspection.id}/documents`, fd);
      toast.success(meta.supersedes_id ? "Neue Version hochgeladen" : "Dokument hochgeladen");
      setMeta({ name: "", doc_type: "protokoll", pruef_date: "", supersedes_id: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const download = async (d) => {
    try {
      const r = await api.get(`/inspections/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.original_filename || d.name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error("Download fehlgeschlagen"); }
  };

  const del = async (d) => {
    try { await api.delete(`/inspections/documents/${d.id}`); toast.success("Dokument gelöscht"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head text-xl flex items-center gap-2"><FileText className="h-5 w-5 text-primary" />Dokumente</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500 -mt-1">{inspection?.title}</p>

        {canWrite && (
          <div className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid="doc-upload-box">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Name</Label><Input value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })} placeholder="Dateiname" className="mt-1" data-testid="doc-name" /></div>
              <div><Label className="text-xs">Typ</Label>
                <Select value={meta.doc_type} onValueChange={(v) => setMeta({ ...meta, doc_type: v })}>
                  <SelectTrigger className="mt-1" data-testid="doc-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Prüfdatum</Label><Input type="date" value={meta.pruef_date} onChange={(e) => setMeta({ ...meta, pruef_date: e.target.value })} className="mt-1" /></div>
            </div>
            {meta.supersedes_id && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Neue Version ersetzt vorheriges Dokument <button className="ml-2 underline" onClick={() => setMeta({ ...meta, supersedes_id: "" })}>abbrechen</button></Badge>}
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx,.doc,.xls" className="hidden" data-testid="doc-file-input" onChange={(e) => upload(e.target.files[0])} />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="doc-upload-btn">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}Datei hochladen
            </Button>
            <p className="text-[11px] text-slate-400">Erlaubt: PDF, JPG, PNG, DOCX, XLSX. Neue Versionen überschreiben nichts – alte Versionen bleiben erhalten.</p>
          </div>
        )}

        {!docs ? <div className="p-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          : docs.length === 0 ? <div className="p-6 text-center text-slate-400 text-sm">Noch keine Dokumente.</div>
          : (
            <div className="space-y-2">
              {docs.map((d) => (
                <div key={d.id} className={`flex items-center gap-2 p-2.5 rounded-lg border ${d.is_latest ? "border-slate-200" : "border-slate-100 bg-slate-50 opacity-70"}`} data-testid={`doc-row-${d.id}`}>
                  <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate flex items-center gap-2">{d.name}
                      <Badge variant="outline" className="text-[10px]">v{d.version}</Badge>
                      {d.is_latest ? <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200">aktuell</Badge> : <Badge variant="outline" className="text-[10px] text-slate-400"><History className="h-2.5 w-2.5 mr-0.5" />alt</Badge>}
                    </div>
                    <div className="text-xs text-slate-400 truncate">{DOC_TYPES.find((t) => t.value === d.doc_type)?.label || d.doc_type}{d.pruef_date ? ` · ${d.pruef_date}` : ""} · {d.uploaded_by}</div>
                  </div>
                  <button onClick={() => download(d)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Download" data-testid={`doc-dl-${d.id}`}><Download className="h-4 w-4" /></button>
                  {canWrite && d.is_latest && <button onClick={() => setMeta({ ...meta, supersedes_id: d.id })} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Neue Version" data-testid={`doc-ver-${d.id}`}><Upload className="h-4 w-4" /></button>}
                  {canDelete && <button onClick={() => del(d)} className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500" title="Löschen" data-testid={`doc-del-${d.id}`}><Trash2 className="h-4 w-4" /></button>}
                </div>
              ))}
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
}
